import { Grid, List, Typography, Box, Avatar, Chip, Paper } from "@mui/material"
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt'
import { useAppDispatch, useAppSelector } from "../../../../../../../hooks/store-hooks";
import { useProjetosService } from "../../../../../../../../use-cases/geo-veles";
import { useEffect, useState } from "react";
import { removeListaCidadesFilterState } from "../../../../../../../../application/states/geo-veles/projeto/projeto-slice";

export const PreviaFiltroLocalizacao = () => {
  // State
  const { projetos, listaCEP, listaCidades } = useAppSelector((state) => state)
  // useState
  const [loading, setLoading] = useState<boolean>(true)
  // Hook
  const {
    removeLocalizacaoFilter,
    removePurgeLocalizacaoFilter,
    removeListaCEPFilter,
    removePurgeListaCEPFilter,
    removePurgeListaCidadesFilter
  } = useProjetosService()

  const dispatch = useAppDispatch();

  useEffect(() => {
    setLoading(false)
  }, [])

  // Helper function to get cities from lists
  const getCidadesNasListas = () => {
    return projetos.filtrosSelecionados?.listasCidades
      ?.flatMap(listaId => {
        const lista = listaCidades.listasCidades?.find(l => l.IdVelesGeoListaCidade === listaId);
        if (!lista?.Cidades) return [];
        
        const cidadesString = lista.Cidades as unknown as string;
        try {
          const cidadesArray = JSON.parse(cidadesString);
          return cidadesArray.map((c: { Nome: string; UF: string }) => c.Nome);
        } catch (e) {
          console.warn('Erro ao parsear Cidades da lista:', lista.IdVelesGeoListaCidade, e);
          return [];
        }
      })
      .flat()
      .filter(Boolean) || [];
  };

  const hasIncludeFilters = () => {
    return (projetos.filtrosSelecionados?.localizacao?.length ?? 0) > 0 ||
           (projetos.filtrosSelecionados?.listasCEP?.length ?? 0) > 0 ||
           (projetos.filtrosSelecionados?.listasCidades?.length ?? 0) > 0;
  };

  const hasExcludeFilters = () => {
    return (projetos.filtrosSelecionados?.purgeLocalizacao?.length ?? 0) > 0 ||
           (projetos.filtrosSelecionados?.purgeListasCEP?.length ?? 0) > 0 ||
           (projetos.filtrosSelecionados?.purgeListasCidades?.length ?? 0) > 0;
  };

  const getUniqueLocalizacoes = () => {
    const cidadesNasListas = getCidadesNasListas();
    return projetos.filtrosSelecionados?.localizacao?.filter(
      loc => !cidadesNasListas.includes(loc.value ?? '')
    ) || [];
  };

  if (loading) return null;

  return (
    <List>
      {/* SEÇÃO INCLUIR */}
      {hasIncludeFilters() && (
        <Paper elevation={2} sx={{ my: 2, p: 2 }}>
          <Typography variant="h6" color="primary" fontWeight="bold" paddingBottom={"10px"}>
            Incluir
          </Typography>

          {/* Listas com Cidades */}
          {(projetos.filtrosSelecionados?.listasCidades?.length ?? 0) > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="primary" fontWeight="bold">
                  Listas de Cidades Selecionadas:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {projetos.filtrosSelecionados?.listasCidades?.map((cidadeId) => {
                  const listaInfo = listaCidades.listasCidades?.find(
                    (l) => l.IdVelesGeoListaCidade === cidadeId
                  );
                  return (
                    <Grid item key={cidadeId} marginBottom={2}>                        
                      <Chip
                        sx={{
                          height: 'auto',
                          '& .MuiChip-label': {
                            transition: '0.1s all',
                            display: 'block',
                            whiteSpace: 'normal',
                            padding: '10px 15px',
                            fontWeight: 'bold',
                          },
                        }}
                        color="primary"
                        onDelete={() =>
                          dispatch(
                            removeListaCidadesFilterState({ idVelesGeoListaCidade: cidadeId })
                          )
                        }
                        avatar={
                          <Avatar style={{ cursor: 'pointer' }}>
                            <EditLocationAltIcon
                              sx={{
                                "&": { transition: '0.1s all' },
                                "&:hover": { color: "secondary.main" },
                              }}
                            />
                          </Avatar>
                        }
                        label={listaInfo?.Nome.toUpperCase() || "LISTA"}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Localizações individuais */}
          {getUniqueLocalizacoes().length > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="primary" fontWeight="bold">
                  Filtros de Localização Selecionados:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {getUniqueLocalizacoes().map((location, index) => (
                  <Grid item key={index} marginBottom={2}>
                    <Chip
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          transition: '0.1s all',
                          display: 'block',
                          whiteSpace: 'normal',
                          padding: '10px 15px',
                          fontWeight: 'bold',
                        },
                      }}
                      color="primary"
                      onDelete={() => removeLocalizacaoFilter(location)}
                      avatar={
                        <Avatar style={{ cursor: 'pointer' }}>
                          <LocationOnIcon
                            sx={{
                              "&": { transition: '0.1s all' },
                              "&:hover": { color: "secondary.main" },
                            }}
                          />
                        </Avatar>
                      }
                      label={location.value}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Listas com CEPs */}
          {(projetos.filtrosSelecionados?.listasCEP?.length ?? 0) > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="primary" fontWeight="bold">
                  Listas com CEPs Selecionadas:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {projetos.filtrosSelecionados?.listasCEP?.map((cepList, index) => (
                  <Grid item key={index} marginBottom={2}>
                    <Chip
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          transition: '0.1s all',
                          display: 'block',
                          whiteSpace: 'normal',
                          padding: '10px 15px',
                          fontWeight: 'bold'
                        }
                      }}
                      color="primary"
                      onDelete={() => removeListaCEPFilter(cepList)}
                      avatar={
                        <Avatar style={{ cursor: 'pointer' }}>
                          <EditLocationAltIcon
                            sx={{
                              "&": { transition: '0.1s all' },
                              "&:hover": { color: "secondary.main" }
                            }} 
                          />
                        </Avatar>
                      }
                      label={listaCEP.listasCEP.find((cepListState) => cepListState.IdVelesGeoListaCEP === cepList)?.Nome.toUpperCase()}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* SEÇÃO NÃO INCLUIR */}
      {hasExcludeFilters() && (
        <Paper elevation={2} sx={{ my: 2, p: 2 }}>
          <Typography variant="h6" color="secondary" fontWeight="bold" paddingBottom={"10px"}>
            Não Incluir
          </Typography>

          {/* Filtros de localização (purge) */}
          {(projetos.filtrosSelecionados?.purgeLocalizacao?.length ?? 0) > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="secondary" fontWeight="bold">
                  Filtros de localização selecionados:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {projetos.filtrosSelecionados?.purgeLocalizacao?.map((location, index) => (
                  <Grid item key={index} marginBottom={2}>
                    <Chip
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          transition: '0.1s all',
                          display: 'block',
                          whiteSpace: 'normal',
                          padding: '10px 15px',
                          fontWeight: 'bold',
                          color: '#000000'
                        }
                      }}
                      color="secondary"
                      onDelete={() => removePurgeLocalizacaoFilter(location)}
                      avatar={
                        <Avatar style={{ cursor: 'pointer' }}>
                          <LocationOnIcon sx={{
                            "&": { transition: '0.1s all' },
                            "&:hover": { color: "primary.main" }
                          }} />
                        </Avatar>
                      }
                      label={location.value}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Listas com CEPs (purge) */}
          {(projetos.filtrosSelecionados?.purgeListasCEP?.length ?? 0) > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="secondary" fontWeight="bold">
                  Listas com CEPs selecionadas:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {projetos.filtrosSelecionados?.purgeListasCEP?.map((cepList, index) => (
                  <Grid item key={index} marginBottom={2}>
                    <Chip
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          transition: '0.1s all',
                          display: 'block',
                          whiteSpace: 'normal',
                          padding: '10px 15px',
                          fontWeight: 'bold'
                        }
                      }}
                      color="secondary"
                      onDelete={() => removePurgeListaCEPFilter(cepList)}
                      avatar={
                        <Avatar style={{ cursor: 'pointer' }}>
                          <EditLocationAltIcon sx={{
                            "&": { transition: '0.1s all' },
                            "&:hover": { color: "primary.main" }
                          }} />
                        </Avatar>
                      }
                      label={listaCEP.listasCEP.find((cepListState) => cepListState.IdVelesGeoListaCEP === cepList)?.Nome.toUpperCase()}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Listas com Cidades (purge) */}
          {(projetos.filtrosSelecionados?.purgeListasCidades?.length ?? 0) > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Typography color="secondary" fontWeight="bold">
                  Listas de Cidades selecionadas:
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {projetos.filtrosSelecionados?.purgeListasCidades?.map((cidadeList, index) => (
                  <Grid item key={index} marginBottom={2}>
                    <Chip
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          transition: '0.1s all',
                          display: 'block',
                          whiteSpace: 'normal',
                          padding: '10px 15px',
                          fontWeight: 'bold'
                        }
                      }}
                      color="secondary"
                      onDelete={() => removePurgeListaCidadesFilter(cidadeList)}
                      avatar={
                        <Avatar style={{ cursor: 'pointer' }}>
                          <EditLocationAltIcon sx={{
                            "&": { transition: '0.1s all' },
                            "&:hover": { color: "primary.main" }
                          }} />
                        </Avatar>
                      }
                      label={listaCidades.listasCidades.find((cidadeListState) => cidadeListState.IdVelesGeoListaCidade === cidadeList)?.Nome.toUpperCase()}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
      )}
    </List>
  )
}